import { ChevronLeft, ChevronRight, Edit2, Plus, Search, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import ConfirmModal from '../model/ConfirmModal';
import Modal from '../model/Modal';

interface FeaturedShow {
  id: number;
  title: string;
  host: string;
  badges: string[];
  description: string;
  time: string;
  image: string;
}

const initialShows: FeaturedShow[] = [
  {
    id: 1,
    title: 'The Morning Show',
    host: 'Sarah Johnson',
    badges: ['News', 'Music', 'Talk'],
    description: 'Wake up to the best music, news, and interviews to kickstart your day.',
    time: 'Weekdays 6-10 AM',
    image: 'https://images.unsplash.com/photo-1627667050069-43757d48d6eb?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxyYWRpbyUyMG1pY3JvcGhvbmUlMjBzdHVkaW98ZW58MXx8fHwxNzY0OTIyNDc2fDA&ixlib=rb-4.1.0&q=80&w=1080'
  },
  {
    id: 2,
    title: 'Weekend Jazz Sessions',
    host: 'Marcus Williams',
    badges: ['Jazz', 'Music', 'Chill'],
    description: 'Smooth jazz classics and modern interpretations for a perfect Saturday night.',
    time: 'Saturdays 8-11 PM',
    image: 'https://images.unsplash.com/photo-1710951403141-353d4e5c7cbf?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxqYXp6JTIwbXVzaWMlMjBwZXJmb3JtYW5jZXxlbnwxfHx8fDE3NjQ5MTU4ODN8MA&ixlib=rb-4.1.0&q=80&w=1080'
  },
  {
    id: 3,
    title: 'The Tech Hour',
    host: 'Rachel Park',
    badges: ['Tech', 'Talk', 'News'],
    description: 'Stay updated with the latest in technology, gadgets, and digital trends.',
    time: 'Thursdays 7-8 PM',
    image: 'https://images.unsplash.com/photo-1707670489413-30382f265e61?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwb2RjYXN0JTIwcmVjb3JkaW5nJTIwaGVhZHBob25lc3xlbnwxfHx8fDE3NjUwMDUzNjF8MA&ixlib=rb-4.1.0&q=80&w=1080'
  }
];

const availableBadges = ['News', 'Music', 'Talk', 'Jazz', 'Chill', 'Tech', 'Sports', 'Culture', 'Comedy', 'Interview'];

export default function FeaturedShowsManager() {
  const [shows, setShows] = useState<FeaturedShow[]>(initialShows);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentShow, setCurrentShow] = useState<FeaturedShow | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  // Confirm Modal State
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    showId: number | null;
  }>({ isOpen: false, showId: null });

  // Badge selection state for modal
  const [selectedBadges, setSelectedBadges] = useState<string[]>([]);
  const [imagePreview, setImagePreview] = useState<string>('');

  const handleCreate = () => {
    setCurrentShow(null);
    setSelectedBadges([]);
    setImagePreview('');
    setIsModalOpen(true);
  };

  const handleEdit = (show: FeaturedShow) => {
    setCurrentShow(show);
    setSelectedBadges(show.badges);
    setImagePreview(show.image);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (id: number) => {
    setConfirmModal({ isOpen: true, showId: id });
  };

  const handleDeleteConfirm = () => {
    if (confirmModal.showId !== null) {
      setShows(shows.filter(s => s.id !== confirmModal.showId));
    }
  };

  const toggleBadge = (badge: string) => {
    if (selectedBadges.includes(badge)) {
      setSelectedBadges(selectedBadges.filter(b => b !== badge));
    } else {
      if (selectedBadges.length < 5) {
        setSelectedBadges([...selectedBadges, badge]);
      }
    }
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    
    const newShow: FeaturedShow = {
      id: currentShow ? currentShow.id : Date.now(),
      title: formData.get('title') as string,
      host: formData.get('host') as string,
      badges: selectedBadges,
      description: formData.get('description') as string,
      time: formData.get('time') as string,
      image: imagePreview || currentShow?.image || 'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?auto=format&fit=crop&q=80'
    };

    if (currentShow) {
      setShows(shows.map(s => s.id === currentShow.id ? newShow : s));
    } else {
      setShows([newShow, ...shows]);
    }
    setIsModalOpen(false);
  };

  // Filtering Logic
  const filteredShows = shows.filter(show => {
    const matchesSearch = show.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          show.host.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  // Pagination Logic
  const totalPages = Math.ceil(filteredShows.length / itemsPerPage);
  const paginatedShows = filteredShows.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Featured Shows</h1>
          <p className="text-slate-400 mt-1">Manage your most popular programs and podcasts.</p>
        </div>
        <button 
          onClick={handleCreate}
          className="flex items-center gap-2 bg-cyan-600 text-white px-5 py-2.5 rounded-xl hover:bg-cyan-500 transition-all shadow-lg shadow-cyan-900/20"
        >
          <Plus className="w-4 h-4" />
          Add Featured Show
        </button>
      </div>

      {/* Search Bar */}
      <div className="bg-[#0B1623] border border-white/10 p-4 rounded-2xl">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search shows by title or host..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-black/20 border border-white/10 rounded-xl focus:outline-none focus:border-cyan-500 text-sm text-white placeholder:text-slate-500 transition-all"
          />
        </div>
      </div>

      {/* Shows Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {paginatedShows.map((show) => (
          <div key={show.id} className="bg-[#0B1623] border border-white/10 rounded-2xl overflow-hidden hover:border-cyan-500/30 hover:shadow-lg hover:shadow-cyan-900/10 transition-all group">
            {/* Image Area */}
            <div className="h-48 relative overflow-hidden">
              <img 
                src={show.image} 
                alt={show.title} 
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" 
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              
              {/* Action Buttons */}
              <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <button 
                  onClick={() => handleEdit(show)}
                  className="p-2 bg-black/60 backdrop-blur-sm text-white hover:bg-cyan-600 rounded-lg transition-colors"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => handleDeleteClick(show.id)}
                  className="p-2 bg-black/60 backdrop-blur-sm text-white hover:bg-red-600 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 pt-0 relative -mt-8">
              {/* Badges */}
              <div className="flex gap-2 mb-4 flex-wrap">
                {show.badges.map((badge, i) => (
                  <span key={i} className="text-xs font-medium text-cyan-100 bg-cyan-900/40 border border-cyan-500/30 px-2.5 py-1 rounded-md backdrop-blur-md">
                    {badge}
                  </span>
                ))}
              </div>

              <h3 className="text-white text-xl font-medium mb-2 group-hover:text-cyan-300 transition-colors">{show.title}</h3>
              <p className="text-cyan-200 text-sm mb-4">with {show.host}</p>
              
              <p className="text-white/60 text-sm mb-6 leading-relaxed line-clamp-3">
                {show.description}
              </p>
              
              <div className="pt-4 border-t border-white/10">
                <p className="text-white/40 text-xs font-medium uppercase tracking-wider">{show.time}</p>
              </div>
            </div>
          </div>
        ))}

        {/* Add New Card */}
        {paginatedShows.length < itemsPerPage && (
          <button 
            onClick={handleCreate}
            className="border-2 border-dashed border-white/10 rounded-2xl p-6 flex flex-col items-center justify-center gap-4 text-slate-500 hover:border-cyan-500/50 hover:text-cyan-400 hover:bg-cyan-500/5 transition-all min-h-[400px] group"
          >
            <div className="w-14 h-14 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-cyan-500/20 group-hover:scale-110 transition-all">
              <Plus className="w-6 h-6" />
            </div>
            <span className="font-medium">Add New Featured Show</span>
          </button>
        )}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-4 mt-8">
          <button 
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="p-2 rounded-lg border border-white/10 text-slate-400 hover:bg-white/5 disabled:opacity-50 disabled:hover:bg-transparent transition-all"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="text-sm text-slate-400">Page {currentPage} of {totalPages}</span>
          <button 
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="p-2 rounded-lg border border-white/10 text-slate-400 hover:bg-white/5 disabled:opacity-50 disabled:hover:bg-transparent transition-all"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Edit/Create Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={currentShow ? 'Edit Featured Show' : 'Add New Featured Show'}
      >
        <form onSubmit={handleSave} className="space-y-5">
          {/* Image URL Input */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Show Image URL</label>
            <input 
              type="url" 
              value={imagePreview}
              onChange={(e) => setImagePreview(e.target.value)}
              className="w-full px-4 py-2.5 bg-black/20 border border-white/10 rounded-xl focus:border-cyan-500 text-white outline-none transition-colors"
              placeholder="https://example.com/image.jpg"
            />
            {imagePreview && (
              <div className="mt-3 rounded-xl overflow-hidden border border-white/10">
                <img src={imagePreview} alt="Preview" className="w-full h-40 object-cover" />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Show Title</label>
            <input 
              type="text" 
              name="title"
              required
              defaultValue={currentShow?.title}
              className="w-full px-4 py-2.5 bg-black/20 border border-white/10 rounded-xl focus:border-cyan-500 text-white outline-none transition-colors"
              placeholder="e.g. The Morning Show"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Host Name</label>
            <input 
              type="text" 
              name="host"
              required
              defaultValue={currentShow?.host}
              className="w-full px-4 py-2.5 bg-black/20 border border-white/10 rounded-xl focus:border-cyan-500 text-white outline-none transition-colors"
              placeholder="e.g. Sarah Johnson"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Categories/Badges <span className="text-slate-500">(Select up to 5)</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {availableBadges.map((badge) => (
                <button
                  key={badge}
                  type="button"
                  onClick={() => toggleBadge(badge)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    selectedBadges.includes(badge)
                      ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/50'
                      : 'bg-white/5 text-slate-400 border border-white/10 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  {badge}
                </button>
              ))}
            </div>
            {selectedBadges.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="text-xs text-slate-500">Selected:</span>
                {selectedBadges.map((badge) => (
                  <span key={badge} className="inline-flex items-center gap-1 px-2 py-1 bg-cyan-900/40 border border-cyan-500/30 text-cyan-300 rounded-md text-xs">
                    {badge}
                    <button
                      type="button"
                      onClick={() => toggleBadge(badge)}
                      className="hover:text-white"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Description</label>
            <textarea 
              name="description"
              required
              defaultValue={currentShow?.description}
              rows={3}
              className="w-full px-4 py-2.5 bg-black/20 border border-white/10 rounded-xl focus:border-cyan-500 text-white outline-none transition-colors resize-none"
              placeholder="Brief description of the show..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Air Time</label>
            <input 
              type="text" 
              name="time"
              required
              defaultValue={currentShow?.time}
              className="w-full px-4 py-2.5 bg-black/20 border border-white/10 rounded-xl focus:border-cyan-500 text-white outline-none transition-colors"
              placeholder="e.g. Weekdays 6-10 AM"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
            <button 
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="px-4 py-2.5 rounded-xl border border-white/10 text-slate-300 hover:bg-white/5 transition-colors"
            >
              Cancel
            </button>
            <button 
              type="submit"
              className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-medium hover:from-cyan-500 hover:to-blue-500 transition-all"
            >
              {currentShow ? 'Update Show' : 'Create Show'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, showId: null })}
        onConfirm={handleDeleteConfirm}
        title="Delete Featured Show"
        message="Are you sure you want to delete this featured show? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isDestructive={true}
      />
    </div>
  );
}
